import { Clipboard } from '@angular/cdk/clipboard'
import { Component, EventEmitter, inject, Input, OnDestroy, Output } from '@angular/core'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { TranslateModule, TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'copy-api-key-modal',
  standalone: true,
  imports: [ModalComponent, TranslateModule],
  templateUrl: './copy-api-key-modal.component.html'
})
export class CopyApiKeyModalComponent implements OnDestroy {
  @Input() id: string = 'copy-api-key-modal'
  @Input() titleKey = 'v2.orgSettings.apiKeys.createApiKeySuccess'
  @Output() closed = new EventEmitter<void>()

  private _token = ''
  private _hasCopied = false

  @Input({ required: true })
  set token(value: string) {
    this._token = value
    this._hasCopied = false
    this._closeCloseWarningModal()
  }

  get token(): string {
    return this._token
  }

  public get closeWarningModalId(): string {
    return `${this.id}-close-warning`
  }

  private readonly _clipboard = inject(Clipboard)
  private readonly _alertService = inject(AlertService)
  private readonly _translate = inject(TranslateService)
  private readonly _modalService = inject(ModalService)

  ngOnDestroy(): void {
    this._closeCloseWarningModal()
  }

  public handleModalClose(): void {
    if (this._hasCopied) {
      this.closed.emit()
      return
    }

    this._openCloseWarningModal()
  }

  public copyToken(): void {
    if (!this.token) return

    this._clipboard.copy(this.token)
    this._hasCopied = true
    this._alertService.alert(Alert.SUCCESS, this._translate.instant('v2.orgSettings.apiKeys.copiedToClipboard'))
  }

  public goBackToCopyModal(): void {
    this._closeCloseWarningModal()
    if (!this._modalService.isModalOpen(this.id)) {
      this._modalService.toggleModal(this.id)
    }
  }

  public closeWithoutCopying(): void {
    this._closeCloseWarningModal()
    this.closed.emit()
  }

  /**
   * Closes the warning and the copy modal without copying the key.
   */
  public handleCloseWarningDismiss(): void {
    this.closeWithoutCopying()
  }

  private _openCloseWarningModal(): void {
    if (!this._modalService.isModalOpen(this.closeWarningModalId)) {
      this._modalService.toggleModal(this.closeWarningModalId)
    }
  }

  private _closeCloseWarningModal(): void {
    if (this._modalService.isModalOpen(this.closeWarningModalId)) {
      this._modalService.toggleModal(this.closeWarningModalId)
    }
  }
}
